import { Check, ChevronDown } from 'lucide-react'
import { useEffect, useId, useRef, useState } from 'react'

export type ProspectingFilterOption = {
  value: string
  label: string
}

type ProspectingFilterProps = {
  ariaLabel: string
  value: string
  options: ProspectingFilterOption[]
  onChange: (value: string) => void
}

export function ProspectingFilter({
  ariaLabel,
  value,
  options,
  onChange,
}: ProspectingFilterProps) {
  const [open, setOpen] = useState(false)
  const rootRef = useRef<HTMLDivElement>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const menuId = useId()

  const selected =
    options.find((option) => option.value === value) ??
    options[0]

  useEffect(() => {
    function handlePointerDown(event: PointerEvent) {
      if (
        rootRef.current &&
        !rootRef.current.contains(event.target as Node)
      ) {
        setOpen(false)
      }
    }

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key !== 'Escape' || !open) return

      event.preventDefault()
      setOpen(false)
      triggerRef.current?.focus()
    }

    document.addEventListener('pointerdown', handlePointerDown)
    document.addEventListener('keydown', handleKeyDown)

    return () => {
      document.removeEventListener('pointerdown', handlePointerDown)
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [open])

  function choose(nextValue: string) {
    onChange(nextValue)
    setOpen(false)

    window.requestAnimationFrame(() => {
      triggerRef.current?.focus()
    })
  }

  return (
    <div
      className="activity-filter prospecting-filter"
      ref={rootRef}
    >
      <button
        aria-controls={menuId}
        aria-expanded={open}
        aria-haspopup="listbox"
        aria-label={ariaLabel}
        className="activity-filter-trigger prospecting-filter-trigger"
        onClick={() => setOpen((current) => !current)}
        ref={triggerRef}
        type="button"
      >
        <span>{selected?.label ?? ''}</span>

        <ChevronDown
          aria-hidden="true"
          className={open ? 'is-open' : ''}
          size={17}
        />
      </button>

      <div
        aria-hidden={!open}
        aria-label={ariaLabel}
        className={`activity-filter-menu prospecting-filter-menu${
          open ? ' is-open' : ''
        }`}
        id={menuId}
        role="listbox"
      >
        {options.map((option) => {
          const isSelected = option.value === value

          return (
            <button
              aria-selected={isSelected}
              className={isSelected ? 'is-selected' : ''}
              key={option.value}
              onClick={() => choose(option.value)}
              role="option"
              tabIndex={open ? 0 : -1}
              type="button"
            >
              <span>{option.label}</span>

              {isSelected ? (
                <Check aria-hidden="true" size={15} />
              ) : null}
            </button>
          )
        })}
      </div>
    </div>
  )
}
